<template>
  <div>
    <v-skeleton-loader v-if="loading" type="card" class="mb-3" />
    <ErrorState v-else-if="error" :message="error" @retry="load" />

    <template v-else-if="detail">
      <!-- ------------------------------------------------------- header --- -->
      <!-- Identity and actions in one band: the logo, what the product is, and
           the two things you might want to do to it. Previously this page could
           only be read -- any change meant going back to the list. -->
      <div class="kf-card neumorphism-flat kf-head">
        <v-btn icon small class="kf-head__back" @click="goBack">
          <LucideIcon name="arrow-left" :size="20" />
        </v-btn>

        <div class="kf-logo" :class="{ 'kf-logo--off': !p.is_active || p.hidden_reason }">
          <img v-if="p.logo" class="kf-logo__img" :src="p.logo" :alt="p.name" />
          <span v-else class="kf-logo__empty">No logo</span>
        </div>

        <div class="kf-head__text">
          <h1 class="kf-head__name">{{ p.name }}</h1>
          <div class="kf-pills">
            <span v-if="p.product_type" class="kf-pill kf-pill--type">{{ p.product_type }}</span>
            <span class="kf-pill" :class="p.is_active ? 'kf-pill--on' : 'kf-pill--off'">
              <span class="kf-pill__dot"></span>
              {{ p.is_active ? "Enabled" : "Disabled" }}
            </span>
            <span v-if="p.flag_recomended" class="kf-pill kf-pill--rec">
              <LucideIcon name="star" :size="12" /> Recommended
            </span>
          </div>
        </div>

        <div class="kf-head__actions">
          <v-btn small outlined class="text-none" @click="form_dialog = true">
            <LucideIcon name="pencil" :size="15" class="mr-1" /> Edit product
          </v-btn>
          <v-btn small outlined class="text-none" :loading="toggling" @click="confirm_dialog = true">
            <LucideIcon name="power" :size="15" class="mr-1" />
            {{ p.is_active ? "Disable" : "Enable" }}
          </v-btn>
        </div>
      </div>

      <!-- --------------------------------------------------- headline ---- -->
      <!-- The numbers someone opens this page to check. StatStrip already
           renders exactly this shape, so it is reused rather than rebuilt. -->
      <StatStrip :items="headline" class="mb-3" />

      <v-row dense>
        <v-col cols="12" md="8">
          <div class="kf-card neumorphism-flat">
            <div class="kf-card__title">Product terms</div>
            <dl class="kf-specs mt-3">
              <div v-for="f in terms" :key="f.label">
                <dt class="kf-specs__label">{{ f.label }}</dt>
                <dd class="kf-specs__value" :class="{ 'kf-specs__value--empty': f.empty }">
                  <a v-if="f.href" :href="f.href" target="_blank" rel="noopener">{{ f.value }}</a>
                  <span v-else>{{ f.value }}</span>
                </dd>
              </div>
            </dl>
          </div>

          <div class="kf-card neumorphism-flat mt-3">
            <div class="kf-card__title">Eligibility rules</div>
            <div class="kf-card__hint">Checked before an offer is generated for a user.</div>
            <dl v-if="p.eligibility" class="kf-specs mt-3">
              <div v-for="f in eligibility" :key="f.label">
                <dt class="kf-specs__label">{{ f.label }}</dt>
                <dd class="kf-specs__value" :class="{ 'kf-specs__value--empty': f.empty }">
                  {{ f.value }}
                </dd>
              </div>
            </dl>
            <!-- Eligibility lives in its own table, so a product can genuinely
                 have none. Saying so beats an empty card. -->
            <div v-else class="kf-none mt-3">
              No eligibility rules are set for this product.
            </div>

            <!-- The serviceable-pincode list can run to thousands of entries,
                 so it is never rendered -- the only useful question about it is
                 "is THIS pincode in it", which is what the server answers. -->
            <div class="kf-pin">
              <span class="kf-pin__label">Check a pincode</span>
              <v-form ref="pinForm" v-model="pin_valid" class="kf-pin__form">
                <v-text-field
                  v-model="pincode"
                  dense
                  outlined
                  hide-details
                  maxlength="6"
                  placeholder="e.g. 492001"
                  :rules="$rules.PIN_CODE"
                  class="kf-pin__field"
                  @keyup.enter="checkPincode"
                />
                <v-btn
                  small
                  depressed
                  color="primary"
                  class="text-none"
                  :loading="pin_checking"
                  @click="checkPincode"
                >
                  Check
                </v-btn>
              </v-form>
              <!-- null, not false, while nothing has been asked: "not
                   serviceable" and "not asked yet" are different answers. -->
              <div
                v-if="pin_result !== null"
                class="kf-pin__result"
                :class="pin_result ? 'kf-pin__result--yes' : 'kf-pin__result--no'"
              >
                <LucideIcon
                  :name="pin_result ? 'circle-check' : 'circle-x'"
                  :size="14"
                  class="mr-1"
                />
                {{ pin_checked }} is
                {{ pin_result ? "serviceable" : "not serviceable" }}
              </div>
            </div>
          </div>
        </v-col>

        <v-col cols="12" md="4">
          <div class="kf-card neumorphism-flat">
            <div class="kf-card__title">Tracking link</div>
            <div class="kf-card__hint">
              <code class="kf-code">{LeadID}</code> and
              <code class="kf-code">{mobile_no}</code> are substituted per user
              when the offer is clicked.
            </div>
            <div v-if="p.utm_link" class="mt-3">
              <!-- Copy sits above the box rather than floating over it: the URL
                   wraps to several lines and an overlaid button lands on top of
                   the text at some widths. -->
              <div class="kf-url__bar">
                <span class="kf-url__caption">Link</span>
                <v-btn x-small text class="text-none" @click="copyLink">
                  <LucideIcon :name="copied ? 'check' : 'copy'" :size="14" class="mr-1" />
                  {{ copied ? "Copied" : "Copy" }}
                </v-btn>
              </div>
              <div class="kf-url">{{ p.utm_link }}</div>
            </div>
            <div v-else class="kf-none mt-3">Not set.</div>
          </div>

          <!-- The performance tiles that used to sit here only ever had demo
               numbers behind them; there is no per-product stats endpoint. An
               unexplained gap reads as a broken page, so say where the numbers
               actually are. -->
          <div class="kf-card neumorphism-flat mt-3 kf-note">
            <LucideIcon name="info" :size="16" class="kf-note__icon" />
            <div>
              Offer and disbursal figures are not measured per product here.
              <router-link :to="{ name: 'karrofin-dashboard' }">Open the dashboard</router-link>
              for funnel and click numbers.
            </div>
          </div>
        </v-col>
      </v-row>

      <ProductFormDialog v-model="form_dialog" :product="p" @saved="load" />

      <ConfirmDialog
        v-model="confirm_dialog"
        :title="p.is_active ? 'Disable this product?' : 'Enable this product?'"
        :confirm-text="p.is_active ? 'Disable' : 'Enable'"
        :tone="p.is_active ? 'error' : 'primary'"
        :loading="toggling"
        @confirm="confirmToggle"
      >
        <span v-if="p.is_active">
          <strong>{{ p.name }}</strong> will stop being offered to users. Nothing
          already disbursed is affected.
        </span>
        <span v-else>
          <strong>{{ p.name }}</strong> will start being offered to eligible users again.
        </span>
      </ConfirmDialog>
    </template>
  </div>
</template>

<script>
import StatStrip from "@/components/karrofin/StatStrip.vue";
import ErrorState from "@/components/karrofin/ErrorState.vue";
import ConfirmDialog from "@/components/karrofin/ConfirmDialog.vue";
import ProductFormDialog from "@/components/karrofin/ProductFormDialog.vue";
import api from "@/app-js/karrofin/api";
import fmt from "@/app-js/karrofin/format";
import { customerPosition } from "@/app-js/karrofin/api/normalise";

/** "-" and "" both mean "nothing recorded" in this data; treat them alike. */
function blank(v) {
  return v === null || v === undefined || v === "" || v === "-";
}

export default {
  name: "KarroFinOfferDetail",
  components: { StatStrip, ErrorState, ConfirmDialog, ProductFormDialog },
  data() {
    return {
      fmt,
      loading: true,
      error: null,
      detail: null,
      form_dialog: false,
      confirm_dialog: false,
      toggling: false,
      copied: false,
      pincode: "",
      pin_valid: false,
      pin_checking: false,
      /** null = not asked yet; true/false = the server's answer. */
      pin_result: null,
      pin_checked: "",
    };
  },
  computed: {
    /** Shorthand -- the template reaches for the product on almost every line. */
    p() {
      return this.detail ? this.detail.product : null;
    },
    headline() {
      const p = this.p;
      // isText on the first three: they are phrases ("3 - 36 months"), not
      // counts, and the number formatter would render them as a dash.
      return [
        {
          label: "Interest rate",
          value: blank(p.interest_rate) ? "Not set" : p.interest_rate,
          isText: true,
        },
        { label: "Tenure", value: blank(p.tenure) ? "Not set" : p.tenure, isText: true },
        { label: "Loan up to", value: this.loanUpto(p.loan_upto), isText: true },
        { label: "Rank", value: p.rank },
        { label: "Customers see", value: this.customerSees, isText: true },
      ];
    },
    /** Where the app and website show it -- Rank counts hidden products too. */
    customerSees() {
      const c = customerPosition(this.p);
      if (c.hidden) return "Hidden · " + c.hidden;
      if (!c.place) return "-";
      return c.types ? `#${c.place} (${c.types})` : `#${c.place}`;
    },
    terms() {
      const p = this.p;
      return [
        // Title is not decoration: offers created before Offers.product existed
        // are matched back to their product BY TITLE (loan.offer_queries), so a
        // product whose title drifted from its name is a real thing to be able
        // to see. It was on the old detail page for that reason.
        this.fact("Title", p.title),
        this.fact("Product type", p.product_type),
        { label: "State", value: p.is_active ? "Enabled" : "Disabled" },
        {
          label: "Manual application",
          value: p.enable_manual_application ? "Allowed" : "Not allowed",
        },
        this.fact("Created", p.created),
        this.fact("Web URL", p.web_url, p.web_url || null),
        { label: "Product ID", value: String(p.id) },
      ];
    },
    eligibility() {
      const e = this.p.eligibility || {};
      const income = blank(e.minimum_income) ? null : fmt.currency(e.minimum_income) + " / mo";
      const maxIncome = blank(e.maximum_income) ? null : fmt.currency(e.maximum_income) + " / mo";
      return [
        // Eligibility is its own row in its own table, and the update endpoint
        // is keyed on this id -- worth showing for the same reason Product ID
        // is.
        this.fact("Eligibility ID", e.id),
        this.fact("Applies to", e.product_type_name),
        {
          label: "Age",
          value:
            blank(e.minimum_age) && blank(e.maximum_age)
              ? "Not set"
              : `${e.minimum_age} – ${e.maximum_age} years`,
          empty: blank(e.minimum_age) && blank(e.maximum_age),
        },
        this.fact("Minimum income", income),
        this.fact("Maximum income", maxIncome),
        this.fact("Minimum CIBIL", e.cibil_score),
        this.fact("Employment", e.employment_type),
        this.fact("Salary type", e.salary_type),
        { label: "Serviceable pincodes", value: fmt.number(e.pincode_count || 0) },
      ];
    },
  },
  watch: {
    "$route.params.id": function () {
      this.load();
    },
  },
  created() {
    this.load();
  },
  methods: {
    /** A fact block, with "Not set" standing in for anything missing. */
    fact(label, value, href) {
      const empty = blank(value);
      return { label, value: empty ? "Not set" : String(value), empty, href: empty ? null : href };
    },
    loanUpto(value) {
      if (blank(value)) return "Not set";
      // A CharField server-side, so older rows hold prose ("Loan upto Rs.20
      // Lakhs"). Show those as written instead of turning them into a dash.
      return isNaN(Number(value)) ? String(value) : fmt.currency(value);
    },
    load() {
      this.loading = true;
      this.error = null;
      api
        .getOfferDetail(this.$route.params.id)
        .then((data) => {
          this.detail = data;
        })
        .catch((err) => {
          this.error = err;
          this.detail = null;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    goBack() {
      // Pop where possible so the list keeps its filters and page; the named
      // route is the fallback for a deep link or a hard refresh.
      if (window.history.length > 1) this.$router.back();
      else this.$router.push({ name: "karrofin-offers" }).catch(() => {});
    },
    checkPincode() {
      if (!this.$refs.pinForm.validate()) {
        this.showSnakeBar("error", "Enter a valid pin code");
        return;
      }
      const asked = this.pincode;
      this.pin_checking = true;
      api
        .checkPincode(this.p.id, asked)
        .then((result) => {
          this.pin_result = !!result.is_serviceable;
          this.pin_checked = asked;
        })
        .catch((err) => {
          this.pin_result = null;
          this.showSnakeBar(
            "error",
            this.getApiErrorMessage(err) || "Failed to check pincode eligibility"
          );
        })
        .finally(() => {
          this.pin_checking = false;
        });
    },
    copyLink() {
      const done = () => {
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 1800);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(this.p.utm_link).then(done).catch(() => {
          this.showSnakeBar("error", "Could not copy the link");
        });
        return;
      }
      // execCommand is deprecated but is the only path on a non-secure origin,
      // which is exactly how this panel is served (plain http on :8086).
      const el = document.createElement("textarea");
      el.value = this.p.utm_link;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      done();
    },
    confirmToggle() {
      this.toggling = true;
      const next = !this.p.is_active;
      api
        .setOfferActive(this.p.id, next)
        .then(() => {
          this.showSnakeBar("success", next ? "Product enabled" : "Product disabled");
          this.confirm_dialog = false;
          this.load();
        })
        .catch((err) => {
          this.showSnakeBar("error", this.getApiErrorMessage(err) || "Could not update the product");
        })
        .finally(() => {
          this.toggling = false;
        });
    },
  },
};
</script>

<style scoped>
/* The pincode lookup. Separated from the rules above by a rule rather than a
   card of its own: it asks a question ABOUT those rules, so it belongs inside
   the same block. */
.kf-pin {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid var(--kf-border);
}
.kf-pin__label {
  font-size: var(--kf-fs-meta);
  font-weight: 700;
  color: #000;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.kf-pin__form {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 8px;
}
.kf-pin__field {
  max-width: 160px;
}
.kf-pin__result {
  display: flex;
  align-items: center;
  margin-top: 8px;
  font-size: var(--kf-fs-hint);
  font-weight: 600;
}
.kf-pin__result--yes {
  color: #00796b;
}
.kf-pin__result--no {
  color: #c62828;
}
.kf-card {
  border-radius: var(--kf-radius);
  padding: 16px 18px;
}
.kf-card__title {
  font-size: 15px;
  font-weight: 700;
  color: var(--kf-brand);
}

/* ------------------------------------------------------------- header --- */
.kf-head {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}
.kf-head__back {
  margin-left: -4px;
}
.kf-head__text {
  min-width: 0;
  flex: 1 1 auto;
}
.kf-head__name {
  font-size: 22px;
  font-weight: 700;
  color: #000;
  line-height: 1.25;
  margin: 0;
}
.kf-head__actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

/* -------------------------------------------------------------- pills --- */
.kf-pills {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 6px;
}
.kf-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: var(--kf-fs-label);
  font-weight: 600;
  padding: 2px 9px;
  border-radius: 999px;
  border: 1px solid transparent;
}
.kf-pill--type {
  background: var(--kf-brand-soft);
  color: var(--kf-brand);
}
.kf-pill--on {
  background: #2e7d321f;
  color: #2e7d32;
}
.kf-pill--off {
  background: #b453091f;
  color: #b45309;
}
.kf-pill--rec {
  background: #b453091f;
  color: #b45309;
}
.kf-pill__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

/* ------------------------------------------------------------ tracking -- */
.kf-code {
  font-size: var(--kf-fs-meta);
  background: var(--kf-code-bg);
  color: var(--kf-brand);
  padding: 1px 5px;
  border-radius: 4px;
}
.kf-url__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}
.kf-url__caption {
  font-size: var(--kf-fs-label);
  font-weight: 600;
  color: #000;
}
.kf-url {
  font-size: var(--kf-fs-meta);
  color: #000;
  background: var(--kf-surface-2);
  border: 1px solid var(--kf-border);
  border-radius: 6px;
  padding: 10px 12px;
  word-break: break-all;
  line-height: 1.5;
}
.kf-none {
  font-size: var(--kf-fs-hint);
  color: #000;
}

/* ---------------------------------------------------------------- note -- */
.kf-note {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: var(--kf-fs-meta);
  color: #000;
  line-height: 1.5;
}
.kf-note__icon {
  flex: 0 0 auto;
  margin-top: 2px;
  color: #000;
}

.kf-specs__value a {
  color: var(--kf-brand);
}
@media (max-width: 600px) {
  .kf-head__name {
    font-size: 19px;
  }
}
</style>
